import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  UpdateRebateInvoiceRequest,
  UpdateRebateInvoiceResponse,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';

/**
 * Hook to update a rebate invoice's invoice number
 */
export function useUpdateRebateInvoice() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    UpdateRebateInvoiceResponse,
    Error,
    UpdateRebateInvoiceRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.transactionRecords.update(input);
    },
    // NOTE: params _data/_variables currently unused (kept via '_' prefix) — revisit and delete if not needed
    onSuccess: (_data, _variables) => {
      // Invalidate transaction record queries (ORPC-based)
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.transactionRecords.key(),
      });
      queryClient.invalidateQueries({
        queryKey: ['rebateAgreements'],
      });

      showToast({
        message: 'Invoice number updated successfully',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update invoice number',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
